import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Send, Check, CheckCheck, Paperclip, Search, ArrowDown, MessageCircle, Crown } from 'lucide-react';

const MessagingPanel = ({ isOpen, onClose, currentUser, targetUser, messages, onSendMessage, onMarkRead }) => {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && onMarkRead) {
      onMarkRead(targetUser?.id);
    }
    scrollToBottom();
  }, [isOpen, messages.length, targetUser?.id]);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 150);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim() || sending || !targetUser?.id) return;
    
    setSending(true);
    try {
      await onSendMessage(targetUser.id, content.trim());
      setContent('');
    } catch (err) {
      alert('Error enviando mensaje: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !targetUser?.id) return;
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
      
      const uploadRes = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!uploadRes.ok) throw new Error('Falló la subida del archivo');
      const uploadData = await uploadRes.json();
      
      // Enviar el link en el mensaje
      await onSendMessage(targetUser.id, `📎 Archivo adjunto: [${uploadData.name}](${uploadData.url})`);
    } catch (err) {
      alert('Error enviando archivo: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentDate = null;

    const filtered = searchQuery
      ? messages.filter(m => m.content.toLowerCase().includes(searchQuery.toLowerCase()))
      : messages;

    filtered.forEach(msg => {
      const msgDate = new Date(msg.createdAt).toLocaleDateString('es-ES', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: 'date', label: getDateLabel(msg.createdAt), key: `date-${msgDate}` });
      }
      groups.push({ type: 'message', data: msg, key: msg.id });
    });
    return groups;
  }, [messages, searchQuery]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="messaging-backdrop"
        onClick={onClose}
      />
      
      {/* Sliding Drawer */}
      <div className="messaging-drawer">
        {/* Header */}
        <div className="messaging-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flex: 1 }}>
            <div className="messaging-avatar">
              {targetUser?.name ? targetUser.name.charAt(0).toUpperCase() : 'U'}
              <span className="messaging-online-dot" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 className="messaging-header-name">{targetUser?.name || 'Chat'}</h3>
              <p className="messaging-header-role">
                {currentUser?.role === 'trainer' ? 'Deportista' : 'Entrenador'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button 
              onClick={() => setShowSearch(!showSearch)} 
              className="messaging-icon-btn"
              title="Buscar mensajes"
            >
              <Search size={18} />
            </button>
            <button onClick={onClose} className="messaging-icon-btn">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search Bar (collapsible) */}
        {showSearch && (
          <div className="messaging-search-bar">
            <Search size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar en la conversación..."
              className="messaging-search-input"
              autoFocus
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="messaging-search-clear">✕</button>
            )}
          </div>
        )}

        {/* Message List */}
        <div 
          className="messaging-chat-area"
          ref={chatContainerRef}
          onScroll={handleScroll}
        >
          {/* Chat pattern overlay */}
          <div className="messaging-chat-pattern" />

          {!targetUser ? (
            <div className="messaging-empty-state">
              <MessageCircle size={48} strokeWidth={1} style={{ color: 'rgba(255,255,255,0.15)', marginBottom: '1rem' }} />
              <p>{currentUser?.role === 'trainer' 
                ? 'Selecciona a un deportista en tu panel para ver vuestros mensajes.' 
                : 'Aún no tienes un entrenador asignado con el que chatear.'}</p>
            </div>
          ) : groupedMessages.length === 0 ? (
            <div className="messaging-empty-state">
              <MessageCircle size={48} strokeWidth={1} style={{ color: 'rgba(255,255,255,0.15)', marginBottom: '1rem' }} />
              <p>{searchQuery ? 'No se encontraron mensajes.' : 'No hay mensajes aún. ¡Inicia la conversación!'}</p>
            </div>
          ) : (
            groupedMessages.map(item => {
              if (item.type === 'date') {
                return (
                  <div key={item.key} className="messaging-date-separator">
                    <span>{item.label}</span>
                  </div>
                );
              }
              const msg = item.data;
              const isMine = msg.senderId === currentUser.id;

              return (
                <div key={item.key} className={`messaging-bubble-wrapper ${isMine ? 'mine' : 'theirs'}`}>
                  <div className={`messaging-bubble ${isMine ? 'mine' : 'theirs'}`}>
                    {renderMessageContent(msg.content)}
                    <span className="messaging-bubble-meta">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {isMine && (
                        msg.isRead 
                          ? <CheckCheck size={14} className="messaging-read-icon read" /> 
                          : <Check size={14} className="messaging-read-icon" />
                      )}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button */}
        {showScrollBtn && (
          <button className="messaging-scroll-btn" onClick={scrollToBottom}>
            <ArrowDown size={18} />
          </button>
        )}

        {/* Input Area */}
        <div className="messaging-input-area">
          <form onSubmit={handleSend} className="messaging-input-form">
            <label className="messaging-attach-btn">
              <Paperclip size={18} />
              <input type="file" disabled={!targetUser || sending} style={{ display: 'none' }} onChange={handleFileUpload} />
            </label>
            <input
              type="text"
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Escribe un mensaje..."
              disabled={!targetUser}
              className="messaging-text-input"
            />
            <button 
              type="submit" 
              disabled={!content.trim() || sending || !targetUser}
              className={`messaging-send-btn ${content.trim() && targetUser ? 'active' : ''}`}
            >
              <Send size={18} style={{ marginLeft: '2px' }} />
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

// Helper: human-readable date label
function getDateLabel(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.setHours(0,0,0,0) - new Date(date).setHours(0,0,0,0)) / 86400000);
  
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return date.toLocaleDateString('es-ES', { weekday: 'long' });
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Helper: parse markdown links for files
function renderMessageContent(content) {
  const fileLinkRegex = /\[(.*?)\]\((.*?)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = fileLinkRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.substring(lastIndex, match.index));
    }
    const [_, text, url] = match;
    const isImage = url.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    
    parts.push(
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer" className="messaging-file-link">
        {text}
      </a>
    );
    lastIndex = fileLinkRegex.lastIndex;
  }
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }
  return <>{parts.length > 0 ? parts : content}</>;
}

export default MessagingPanel;
